import { supabase } from './supabaseClient';
import { serviceRoleClient } from './serviceRoleClient';

export class NotificationService {
  static async getAllUserIds() {
    try {
      const { data, error } = await supabase.rpc('admin_get_all_users_all');
      if (error) throw error;
      return (data || []).map(u => u.id).filter(Boolean);
    } catch (e) {
      console.error('getAllUserIds error:', e);
      return [];
    }
  }

  static async getUserIdsByRole(roleName) {
    try {
      const { data, error } = await supabase.rpc('user_management_get_users_by_role', {
        p_role: roleName,
        p_page: 1,
        p_limit: 10000,
      });
      if (error) throw error;
      return (data || []).map(u => u.id).filter(Boolean);
    } catch (e) {
      console.error('getUserIdsByRole error:', e);
      return [];
    }
  }

  static async sendBroadcast({ title, message, category, targetRole, sentBy }) {
    try {
      let userIds = [];
      if (targetRole === 'all') {
        userIds = await this.getAllUserIds();
      } else {
        userIds = await this.getUserIdsByRole(targetRole);
      }

      if (userIds.length === 0) {
        return { success: false, sent: 0, error: 'No users found for the selected role' };
      }

      const safeCategory = [
        'service_request', 'payment', 'chat', 'system', 'reminder',
        'shipment', 'shipping', 'delivery', 'order', 'booking',
        'courier', 'tracking', 'shipment_update',
      ].includes(category) ? category : 'system';

      const { data, error } = await serviceRoleClient.rpc('notification_create_bulk', {
        p_user_ids: userIds,
        p_title: title,
        p_message: message,
        p_type: 'info',
        p_category: safeCategory,
        p_data: { broadcast: true, sent_by: sentBy, timestamp: new Date().toISOString() },
      });

      if (error) throw error;

      const sent = data?.sent || userIds.length;

      await serviceRoleClient.rpc('notification_log_broadcast', {
        p_title: title,
        p_message: message,
        p_category: safeCategory,
        p_target_role: targetRole,
        p_sent_by: sentBy || null,
        p_recipient_count: sent,
      });

      return { success: true, sent };
    } catch (e) {
      console.error('sendBroadcast error:', e);
      return { success: false, sent: 0, error: e.message };
    }
  }

  static async getBroadcastHistory(limit = 50, offset = 0) {
    try {
      const { data, error } = await supabase.rpc('notification_list_broadcasts', {
        p_limit: limit,
        p_offset: offset,
      });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('getBroadcastHistory error:', e);
      return [];
    }
  }

  static async listTemplates(category, targetRole) {
    try {
      const { data, error } = await supabase.rpc('notification_template_list', {
        p_category: category || null,
        p_target_role: targetRole || null,
      });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('listTemplates error:', e);
      return [];
    }
  }

  static async upsertTemplate({ id, title, message, category, targetRole }) {
    try {
      const { data, error } = await supabase.rpc('notification_template_upsert', {
        p_title: title,
        p_message: message,
        p_category: category || 'general',
        p_target_role: targetRole || 'all',
        p_id: id || null,
      });
      if (error) throw error;
      return Array.isArray(data) ? data[0] : data;
    } catch (e) {
      console.error('upsertTemplate error:', e);
      return null;
    }
  }

  static async deleteTemplate(id) {
    try {
      const { error } = await supabase.rpc('notification_template_delete', { p_id: id });
      if (error) throw error;
      return true;
    } catch (e) {
      console.error('deleteTemplate error:', e);
      return false;
    }
  }
}
